import { CardComponentProps } from "@~/types/types";
import { useEffect } from "react";
import { useExperienceString } from "@~/flags/flags";

const COMPARE_SCRIPT_ID = "jl-compare-script";
const COMPARE_STYLESHEET_ID = "jl-compare-style";

const ensureCompareWidgetScriptLoaded = () => {
  if (
    !window.customElements.get("jl-compare") &&
    !document.getElementById(COMPARE_SCRIPT_ID)
  ) {
    const script = document.createElement("script");
    script.src = "/compare/compare.iife.js";
    script.id = COMPARE_SCRIPT_ID;
    document.head.appendChild(script);
  }
};

const ensureCompareWidgetStylesheetLoaded = () => {
  if (!document.getElementById(COMPARE_STYLESHEET_ID)) {
    const stylesheet = document.createElement("link");
    stylesheet.id = COMPARE_STYLESHEET_ID;
    stylesheet.rel = "stylesheet";
    stylesheet.href = "/compare/style.css";
    document.head.appendChild(stylesheet);
  }
};

const ensureCompareWidgetHostMounted = (experiencesRawString?: string) => {
  const existingHost = document.querySelector("jl-compare");
  const compareHost = existingHost || document.createElement("jl-compare");

  compareHost.setAttribute(
    "experiences-raw-string",
    experiencesRawString || "",
  );

  if (!existingHost) {
    document.body.appendChild(compareHost);
  }
};

const ensureCompareWidgetBootstrapped = (experiencesRawString?: string) => {
  ensureCompareWidgetScriptLoaded();
  ensureCompareWidgetStylesheetLoaded();
  ensureCompareWidgetHostMounted(experiencesRawString);
};

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace React.JSX {
    export interface IntrinsicElements {
      "jl-compare-checkbox": {
        "data-testid"?: string;
        "pim-product-id"?: string;
        title?: string;
        "ad-id"?: string;
        "kevel-ad-id"?: string;
        "click-url"?: string;
        "impression-url"?: string;
      };
    }
  }
}

export function CompareCheckbox({
  product,
}: Pick<CardComponentProps, "product">) {
  const experiencesRawString = useExperienceString();

  useEffect(
    function bootstrapCompareWidget() {
      if (product.compare) {
        ensureCompareWidgetBootstrapped(experiencesRawString);
      }
    },
    [experiencesRawString, product.compare],
  );

  if (!product.compare) {
    return null;
  }

  const isKevelProduct = product.advertising?.clickUrl !== undefined;

  return (
    <jl-compare-checkbox
      data-testid="compare-checkbox"
      data-component="pcl-CompareCheckbox"
      pim-product-id={product.aliases.pimProductId}
      title={product.title}
      ad-id={
        !isKevelProduct ? product.advertising?.associatedAdvertId : undefined
      }
      kevel-ad-id={
        isKevelProduct ? product.advertising?.associatedAdvertId : undefined
      }
      click-url={isKevelProduct ? product.advertising?.clickUrl : undefined}
      impression-url={
        isKevelProduct ? product.advertising?.impressionUrl : undefined
      }
    />
  );
}
